import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Icon } from "@/components/Icon"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useCases } from "@/features/cases/hooks/useCases"
import { toCaseVM, TYPE_ICON, STATUS_PILL, type CaseVM } from "@/features/cases/case-view"
import { confirmAction } from "@/shell/confirm.store"
import { useGenerationStore } from "@/features/oio/generation.store"
import type { Thread } from "@/types"

const FILTERS: Array<[string, string]> = [
  ["all", "All"],
  ["review", "Ready for review"],
  ["draft", "Drafts"],
  ["filed", "Filed"],
]

// Build one lowercased search string per case from its display fields AND its
// raw thread metadata (file names, GSTIN, demand, etc.) so officers can find a
// case by any detail. Skip urls / ids / secrets — they're noise, not searchable.
function caseHaystack(c: CaseVM, thread: Thread): string {
  const m = (thread.metadata ?? {}) as Record<string, unknown>
  const metaVals = Object.entries(m)
    .filter(([k]) => !/url$|_id$|aes|secret|token|password/i.test(k))
    .map(([, v]) => (typeof v === "string" || typeof v === "number" ? String(v) : ""))
    .join(" ")
  return [c.title, c.ref, c.typeLabel, c.statusLabel, c.officer, c.amount, metaVals]
    .join(" ")
    .toLowerCase()
}

export function VaultView() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const { cases, setActiveCase, deleteCase } = useCases()
  const runs = useGenerationStore((s) => s.runs)
  const [filter, setFilter] = useState("all")
  const [query, setQuery] = useState("")
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const rows = useMemo(
    () =>
      cases.map((t) => {
        const c = toCaseVM(t, session?.user)
        return { c, hay: caseHaystack(c, t) }
      }),
    [cases, session],
  )
  const vms = rows.map((r) => r.c)

  const q = query.trim().toLowerCase()
  const shown = rows
    .filter(({ c, hay }) => {
      const matchesFilter =
        filter === "all" ||
        c.status === filter ||
        (filter === "draft" && c.status === "progress")
      const matchesQuery = !q || hay.includes(q)
      return matchesFilter && matchesQuery
    })
    .map((r) => r.c)

  const openCase = (id: string) => {
    const thread = cases.find((c) => c.id === id)
    if (thread) setActiveCase(thread)
    navigate("/drafting")
  }

  const removeCase = (id: string, title: string) =>
    confirmAction({
      title: "Delete case?",
      message: `"${title}" and its draft will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete case",
      danger: true,
      onConfirm: async () => {
        setDeletingId(id)
        try {
          await deleteCase(id)
        } finally {
          setDeletingId(null)
        }
      },
    })

  const open = vms.length
  const inReview = vms.filter((c) => c.status === "review").length
  const filed = vms.filter((c) => c.status === "filed").length

  return (
    <>
      <div className="topbar">
        <span className="topbar__title">Vault</span>
        <span className="topbar__sub">Cases &amp; documents repository</span>
        <div className="topbar__spacer" />
        <button className="btn btn--primary btn--sm" onClick={() => navigate("/ask")}>
          <Icon name="plus" size={14} /> Open simulators
        </button>
      </div>

      <div className="vault">
        <div className="vault__inner">
          <div className="statline">
            <Stat v={String(open)} u="open" l="Active adjudications" />
            <Stat v={String(inReview)} u="" l="Awaiting your review" />
            <Stat v={String(filed)} u="" l="Orders filed" />
            <Stat v={String(cases.length)} u="" l="Total cases" />
          </div>

          <div className="vault__tools">
            <div className="search">
              <Icon name="search" size={16} />
              <input
                placeholder="Search by case name, reference, GSTIN, file name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="seg" role="tablist" aria-label="Filter by status">
              {FILTERS.map(([k, l]) => (
                <button
                  key={k}
                  className={filter === k ? "is-active" : ""}
                  onClick={() => setFilter(k)}
                >
                  {l}
                </button>
              ))}
            </div>
            <button className="btn btn--ghost">
              <Icon name="filter" size={15} /> Sort
            </button>
          </div>

          {shown.length === 0 ? (
            <div className="empty" style={{ height: "auto", padding: "60px 20px" }}>
              <div className="empty__ico">
                <Icon name="archive" size={22} />
              </div>
              <h3>No matching cases</h3>
              <p>
                {cases.length === 0
                  ? "Start a new draft to create your first case."
                  : "Try a different search or filter."}
              </p>
            </div>
          ) : (
            <table className="vtable">
              <thead>
                <tr>
                  <th style={{ width: "38%" }}>Workspace</th>
                  <th>Type</th>
                  <th>Demand</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th aria-label="Actions" style={{ width: 44 }}></th>
                </tr>
              </thead>
              <tbody>
                {shown.map((c) => (
                  <Row
                    key={c.id}
                    c={c}
                    onOpen={openCase}
                    onDelete={removeCase}
                    deleting={deletingId === c.id}
                    running={runs[c.id]?.status === "running"}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  )
}

function Stat({ v, u, l }: { v: string; u: string; l: string }) {
  return (
    <div className="statcard">
      <div className="sv">
        {v} {u && <span className="u">{u}</span>}
      </div>
      <div className="sl">{l}</div>
    </div>
  )
}

function Row({
  c,
  onOpen,
  onDelete,
  deleting,
  running,
}: {
  c: CaseVM
  onOpen: (id: string) => void
  onDelete: (id: string, title: string) => void
  deleting: boolean
  running: boolean
}) {
  return (
    <tr onClick={() => onOpen(c.id)} className="vt-row">
      <td>
        <div className="vt-title">
          {running && <span className="run-dot" title="Generating" />}
          {c.title}
        </div>
        <div className="vt-sub mono">{c.ref}</div>
      </td>
      <td>
        <span className="vt-type">
          <span className="ti">
            <Icon name={TYPE_ICON[c.type]} size={14} />
          </span>
          {c.typeLabel}
        </span>
      </td>
      <td className="vt-amt">{c.amount}</td>
      <td>
        {running ? (
          <span className="pill pill--partial">
            <span className="run-dot" /> Drafting
          </span>
        ) : (
          <span className={"pill pill--" + STATUS_PILL[c.status]}>{c.statusLabel}</span>
        )}
      </td>
      <td className="mono" style={{ color: "var(--ink-3)", fontSize: 12 }}>
        {c.updated}
      </td>
      <td>
        <button
          className="vt-del"
          title="Delete case"
          aria-label={`Delete ${c.title}`}
          disabled={deleting}
          onClick={(e) => {
            e.stopPropagation()
            onDelete(c.id, c.title)
          }}
        >
          {deleting ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Icon name="trash" size={15} />}
        </button>
      </td>
    </tr>
  )
}
